<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="config.title"
    :width="type === 'buy-card' ? '520px' : '440px'"
    :custom-class="type === 'buy-card' ? 'up-action-dialog up-point-card-dialog' : 'up-action-dialog'"
    append-to-body
    :close-on-click-modal="false"
  >
    <div v-if="type !== 'buy-card'" class="action-dialog__intro">
      <span :class="config.tone"><i :class="config.icon" /></span>
      <div><strong>{{ config.title }}</strong><small>{{ config.description }}</small></div>
    </div>

    <template v-if="type === 'transfer'">
      <div class="transfer-desktop-fields">
        <label>转出账户</label>
        <el-select v-model="form.from" class="transfer-fixed-select" disabled style="width: 100%">
          <el-option label="资金账户" value="1" />
          <el-option label="燃料账户" value="2" />
        </el-select>
        <small class="transfer-available">
          可用余额：
          <strong>{{ availableBalanceText }}</strong>
          USDT
        </small>
        <label>转入账户</label>
        <el-select v-model="form.to" class="transfer-fixed-select" disabled style="width: 100%">
          <el-option label="燃料账户" value="2" />
          <el-option label="资金账户" value="1" />
        </el-select>
      </div>

      <div class="transfer-mobile-flow" aria-label="账户划转方向">
        <div class="transfer-mobile-flow__account">
          <span>转出 · {{ transferSourceName }}</span>
          <strong>{{ availableBalanceText }}</strong>
        </div>
        <i class="el-icon-right" aria-hidden="true" />
        <div class="transfer-mobile-flow__account is-target">
          <span>转入 · {{ transferTargetName }}</span>
          <strong>{{ targetBalanceText }}</strong>
        </div>
      </div>
    </template>

    <template v-if="type === 'withdraw'">
      <label>提现地址</label>
      <el-input v-model.trim="form.address" placeholder="请输入 USDT 钱包地址" />
      <label>验证码</label>
      <div class="verification-row">
        <el-input
          v-model.trim="form.verificationCode"
          inputmode="numeric"
          maxlength="6"
          autocomplete="one-time-code"
          placeholder="请输入6位验证码"
          @input="normalizeVerificationCode"
        />
        <button class="up-ghost-button" type="button" @click="$emit('send-code', { type: 'withdraw' })">发送验证码</button>
      </div>
      <small class="verification-target">接收账号：{{ userAccount || '--' }}</small>
    </template>

    <template v-if="type === 'internal-withdraw'">
      <label>{{ $t('收款账户类型') }}</label>
      <el-select v-model="form.accountType" style="width: 100%">
        <el-option :label="$t('手机号')" value="mobile" />
        <el-option :label="$t('邮箱')" value="email" />
      </el-select>
      <label>{{ $t('对方 UPlus 平台账户') }}</label>
      <el-input
        v-model.trim="form.targetAccount"
        :type="form.accountType === 'mobile' ? 'tel' : 'text'"
        :maxlength="form.accountType === 'mobile' ? 11 : 254"
        :placeholder="internalTargetPlaceholder"
      />
    </template>
    <template v-if="type === 'buy-card'">
      <div class="point-card-account">
        <span>支付账户 · 资金账户</span>
        <strong>可用 {{ pointCardBalanceText }} USDT</strong>
      </div>
      <label>购买额度 (USDT)</label>
      <el-select
        v-model="form.cardIndex"
        class="point-card-select"
        style="width: 100%"
        :loading="pointCardsLoading"
        :disabled="pointCardsLoading || !pointCards.length"
        placeholder="请选择购买额度"
      >
        <el-option
          v-for="(card, index) in pointCards"
          :key="`${card.card_point}-${index}`"
          :label="cardLabel(card)"
          :value="index"
        />
      </el-select>
      <div v-if="pointCardsLoading" class="point-card-state">
        <i class="el-icon-loading" />
        正在加载可购买额度…
      </div>
      <div v-else-if="!pointCards.length" class="point-card-state">
        暂无可购买额度
      </div>
      <p class="point-card-estimate">
        预计获得 <strong>{{ expectedPointCardPointsText }}</strong> 点
        <span>（1 USDT = 1 点）</span>
      </p>
      <div class="point-card-notice">
        点卡仅用于抵扣平台服务费，不可提现、退换或转让；消费时优先于燃料账户扣除。
      </div>
    </template>

    <label v-if="showAmount">{{ amountLabel }}</label>
    <el-input
      v-if="showAmount"
      v-model.trim="form.amount"
      :class="{ 'has-all-button': isWithdrawalAction }"
      inputmode="decimal"
      placeholder="0.00"
    >
      <span slot="suffix" class="action-dialog__amount-suffix">
        <span class="action-dialog__unit">USDT</span>
        <button
          v-if="isWithdrawalAction"
          class="action-dialog__all"
          type="button"
          :disabled="withdrawableBalance === null"
          @mousedown.prevent
          @click="fillWithdrawableAmount"
        >
          全部
        </button>
      </span>
    </el-input>
    <small v-if="isWithdrawalAction" class="withdraw-available">
      可提现金额：
      <strong>{{ withdrawableBalanceText }}</strong>
      USDT
    </small>
    <div v-if="type === 'internal-withdraw'" class="internal-transfer-fee">
      <span>{{ $t('手续费') }}</span>
      <del>{{ internalOriginalFeeText }} USDT</del>
      <strong>{{ $t('限时免费') }}</strong>
    </div>

    <template v-if="type === 'internal-withdraw'">
      <label>{{ $t('验证码') }}</label>
      <div class="verification-row">
        <el-input
          v-model.trim="form.verificationCode"
          inputmode="numeric"
          maxlength="6"
          autocomplete="one-time-code"
          :placeholder="$t('请输入6位验证码')"
          @input="normalizeVerificationCode"
        />
        <button class="up-ghost-button" type="button" @click="$emit('send-code', { type: 'internal-withdraw' })">
          {{ $t('发送验证码') }}
        </button>
      </div>
      <small class="verification-target">{{ $t('接收账号') }}：{{ userAccount || '--' }}</small>
    </template>

    <span
      slot="footer"
      class="dialog-footer"
      :class="{ 'point-card-footer': type === 'buy-card' }"
    >
      <button class="up-ghost-button" type="button" @click="dialogVisible = false">取消</button>
      <button
        class="up-primary-button"
        type="button"
        :disabled="submitting || (type === 'buy-card' && pointCardsLoading)"
        @click="submit"
      >
        {{ submitting ? '处理中…' : config.confirm }}
      </button>
    </span>
  </el-dialog>
</template>

<script>
import { languageIntlLocale } from '@/utils/locale'

const configs = {
  withdraw: { title: '链上提现 USDT', description: '提现至 Tron (TRC20) 钱包地址', icon: 'el-icon-upload2', tone: 'orange', confirm: '确认提现' },
  'internal-withdraw': { title: '内部转账', description: '转至其他 UPlus 用户的资金账户', icon: 'el-icon-user', tone: 'blue', confirm: '确认转账' },
  transfer: { title: '账户划转', description: '资金账户与燃料账户之间划转', icon: 'el-icon-sort', tone: 'green', confirm: '确认划转' },
  'buy-card': { title: '购买点卡', description: '', icon: 'el-icon-bank-card', tone: 'orange', confirm: '确认购买' }
}

export default {
  name: 'ActionModal',
  props: {
    visible: { type: Boolean, default: false },
    type: { type: String, default: 'transfer' },
    submitting: { type: Boolean, default: false },
    pointCards: { type: Array, default: () => [] },
    pointCardsLoading: { type: Boolean, default: false },
    userAccount: { type: String, default: '' },
    userEmail: { type: String, default: '' },
    userMobile: { type: String, default: '' },
    initialTransferFrom: { type: String, default: '1' },
    balances: {
      type: Object,
      default: () => ({ fund: null, fuel: null })
    }
  },
  data() {
    return {
      form: {
        from: '1',
        to: '2',
        address: '',
        amount: '',
        verificationCode: '',
        accountType: 'email',
        targetAccount: '',
        cardIndex: null
      }
    }
  },
  computed: {
    dialogVisible: {
      get() { return this.visible },
      set(value) { this.$emit('update:visible', value) }
    },
    config() {
      const config = configs[this.type] || configs.transfer
      return {
        ...config,
        title: this.$t(config.title),
        description: this.$t(config.description),
        confirm: this.$t(config.confirm)
      }
    },
    isWithdrawalAction() {
      return ['withdraw', 'internal-withdraw'].includes(this.type)
    },
    showAmount() {
      return ['withdraw', 'internal-withdraw', 'transfer'].includes(this.type)
    },
    amountLabel() {
      return this.type === 'internal-withdraw' ? this.$t('转账金额') : this.$t('金额')
    },
    internalTargetPlaceholder() {
      return this.form.accountType === 'email'
        ? this.$t('请输入对方邮箱')
        : this.$t('请输入对方手机号')
    },
    internalOriginalFeeText() {
      const amount = Number(this.form.amount)
      if (!Number.isFinite(amount) || amount <= 0) return '0.00'
      return (amount * 0.5).toLocaleString(languageIntlLocale(), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    availableBalance() {
      const key = this.form.from === '2' ? 'fuel' : 'fund'
      const rawValue = this.balances[key]
      if (rawValue === null || rawValue === undefined || rawValue === '') return null
      const value = Number(rawValue)
      return Number.isFinite(value) ? value : null
    },
    availableBalanceText() {
      if (this.availableBalance === null) return '--'
      return this.availableBalance.toLocaleString(languageIntlLocale(), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8
      })
    },
    transferSourceName() {
      return this.form.from === '2' ? this.$t('燃料账户') : this.$t('资金账户')
    },
    transferTargetName() {
      return this.form.to === '2' ? this.$t('燃料账户') : this.$t('资金账户')
    },
    targetBalanceText() {
      const key = this.form.to === '2' ? 'fuel' : 'fund'
      const rawValue = this.balances[key]
      if (rawValue === null || rawValue === undefined || rawValue === '') return '--'
      const value = Number(rawValue)
      if (!Number.isFinite(value)) return '--'
      return value.toLocaleString(languageIntlLocale(), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8
      })
    },
    withdrawableBalance() {
      const rawValue = this.balances.fund
      if (rawValue === null || rawValue === undefined || rawValue === '') return null
      const value = Number(rawValue)
      return Number.isFinite(value) ? Math.max(0, value) : null
    },
    withdrawableBalanceText() {
      if (this.withdrawableBalance === null) return '--'
      return this.withdrawableBalance.toLocaleString(languageIntlLocale(), {
        minimumFractionDigits: 2,
        maximumFractionDigits: 8
      })
    },
    pointCardBalanceText() {
      return this.withdrawableBalanceText
    },
    selectedPointCard() {
      if (
        this.form.cardIndex === '' ||
        this.form.cardIndex === null ||
        this.form.cardIndex === undefined
      ) {
        return null
      }
      const index = Number(this.form.cardIndex)
      if (!Number.isInteger(index) || index < 0) return null
      return this.pointCards[index] || null
    },
    expectedPointCardPointsText() {
      if (!this.selectedPointCard) return '0'
      const amount = Number(this.selectedPointCard.card_point)
      const creditedAmount = Number(this.selectedPointCard.give_card)
      const pointCardAmount = Number.isFinite(creditedAmount) && creditedAmount > 0
        ? creditedAmount
        : amount
      if (!Number.isFinite(pointCardAmount)) return '0'
      return pointCardAmount.toLocaleString(languageIntlLocale(), {
        maximumFractionDigits: 2
      })
    }
  },
  watch: {
    visible(value) {
      if (value && this.type === 'transfer') this.resetTransferAccounts()
      if (value && this.type === 'buy-card') this.form.cardIndex = null
      if (value && this.isWithdrawalAction) this.resetWithdrawalForm()
    },
    'form.from'(value) {
      if (this.type === 'transfer') {
        this.form.to = value === '2' ? '1' : '2'
      }
    },
    'form.accountType'() {
      if (this.type === 'internal-withdraw') this.form.targetAccount = ''
    },
    type() {
      this.form.amount = ''
      this.form.address = ''
      this.form.verificationCode = ''
      this.form.accountType = 'email'
      this.form.targetAccount = ''
      this.form.cardIndex = null
    }
  },
  methods: {
    resetWithdrawalForm() {
      this.form.address = ''
      this.form.amount = ''
      this.form.verificationCode = ''
      this.form.accountType = 'email'
      this.form.targetAccount = ''
    },
    resetTransferAccounts() {
      this.form.from = this.initialTransferFrom === '2' ? '2' : '1'
      this.form.to = this.form.from === '1' ? '2' : '1'
      this.form.amount = ''
    },
    submit() {
      if (this.type === 'buy-card') {
        const card = this.pointCards[this.form.cardIndex]
        if (!card) {
          this.$message.warning(this.$t('请选择购买额度'))
          return
        }
        const cardAmount = Number(card.card_point)
        if (!Number.isFinite(cardAmount) || cardAmount <= 0) {
          this.$message.warning(this.$t('当前购买额度无效'))
          return
        }
        if (
          this.withdrawableBalance !== null &&
          cardAmount > this.withdrawableBalance
        ) {
          this.$message.warning(this.$t('资金账户余额不足'))
          return
        }
        this.$emit('submit', {
          type: this.type,
          cardAmount,
          giveCard: Number(card.give_card || 0)
        })
        return
      }
      if (this.type === 'withdraw') {
        const withdrawAmount = Number(this.form.amount)
        if (!Number.isFinite(withdrawAmount) || withdrawAmount <= 0) {
          this.$message.warning(this.$t('请输入提现金额'))
          return
        }
        if (withdrawAmount < 50) {
          this.$message.warning(this.$t('提现金额不能小于50USDT'))
          return
        }
        if (
          this.withdrawableBalance !== null &&
          withdrawAmount > this.withdrawableBalance
        ) {
          this.$message.warning(this.$t('提现金额不能超过可提现金额'))
          return
        }
        if (!this.form.address) {
          this.$message.warning(this.$t('请输入提现地址'))
          return
        }
        if (
          this.form.address.charAt(0) !== 'T' ||
          this.form.address.length !== 34
        ) {
          this.$message.warning(this.$t('提现地址不正确'))
          return
        }
        if (!this.form.verificationCode) {
          this.$message.warning(this.$t('请输入验证码'))
          return
        }
        if (!/^\d{6}$/.test(this.form.verificationCode)) {
          this.$message.warning(this.$t('请输入6位验证码'))
          return
        }
        this.$emit('submit', {
          type: this.type,
          ...this.form,
          amount: withdrawAmount
        })
        return
      }

      if (this.type === 'internal-withdraw') {
        const targetAccount = String(this.form.targetAccount || '').trim()
        if (!targetAccount) {
          this.$message.warning(this.$t('请输入对方账户'))
          return
        }
        if (this.form.accountType === 'email') {
          const emailPattern = /^\w+(?:\.\w+){0,1}@[a-zA-Z0-9]{2,14}(?:\.[a-z]{2,4}){1,2}$/
          if (!emailPattern.test(targetAccount)) {
            this.$message.warning(this.$t('邮箱格式不正确'))
            return
          }
        } else if (!/^1[3-9][0-9]{9}$/.test(targetAccount)) {
          this.$message.warning(this.$t('手机号格式不正确'))
          return
        }
        if (
          targetAccount === String(this.userEmail || '').trim() ||
          targetAccount === String(this.userMobile || '').trim()
        ) {
          this.$message.warning(this.$t('不能转账给自己'))
          return
        }
        const transferAmount = Number(this.form.amount)
        if (!Number.isFinite(transferAmount) || transferAmount <= 0) {
          this.$message.warning(this.$t('请输入转账金额'))
          return
        }
        if (
          this.withdrawableBalance !== null &&
          transferAmount > this.withdrawableBalance
        ) {
          this.$message.warning(this.$t('转账金额不能超过资金账户余额'))
          return
        }
        if (!/^\d{6}$/.test(this.form.verificationCode)) {
          this.$message.warning(this.$t('请输入6位验证码'))
          return
        }
        this.$emit('submit', {
          type: this.type,
          address: targetAccount,
          accountType: this.form.accountType,
          amount: transferAmount,
          verificationCode: this.form.verificationCode
        })
        return
      }
      const amount = Number(this.form.amount)
      if (!Number.isFinite(amount) || amount <= 0) {
        this.$message.warning(this.$t('请输入有效金额'))
        return
      }
      if (this.type === 'transfer' && this.form.from === this.form.to) {
        this.$message.warning(this.$t('转入和转出账户不能相同'))
        return
      }
      if (
        this.type === 'transfer' &&
        this.availableBalance !== null &&
        amount > this.availableBalance
      ) {
        this.$message.warning(this.$t('划转金额不能超过可用余额'))
        return
      }
      this.$emit('submit', { type: this.type, ...this.form, amount })
    },
    fillWithdrawableAmount() {
      if (this.withdrawableBalance === null) {
        this.$message.warning(this.$t('可提现金额尚未加载'))
        return
      }
      this.form.amount = String(this.withdrawableBalance)
    },
    normalizeVerificationCode(value) {
      this.form.verificationCode = String(value || '')
        .replace(/\D/g, '')
        .slice(0, 6)
    },
    cardLabel(card) {
      const amount = Number(card.card_point)
      const give = Number(card.give_card || 0)
      const bonus = Number.isFinite(give) && give > amount ? give - amount : 0
      const amountText = Number.isFinite(amount)
        ? amount.toLocaleString(languageIntlLocale(), { maximumFractionDigits: 8 })
        : '--'
      const bonusText = bonus > 0
        ? `（赠送 ${bonus.toLocaleString(languageIntlLocale(), { maximumFractionDigits: 8 })} USDT额度）`
        : ''
      return `${amountText} USDT${bonusText}`
    }
  }
}
</script>

<style>
.up-action-dialog { max-width: calc(100vw - 28px); border-radius: 14px; }
.up-action-dialog .el-dialog__header { padding: 20px 22px 13px; border-bottom: 1px solid #edf0f5; }
.up-action-dialog .el-dialog__title { color: #1c2333; font-size: 16px; font-weight: 800; }
.up-action-dialog .el-dialog__body { padding: 20px 22px 6px; }
.up-action-dialog .el-dialog__footer { padding: 14px 22px 20px; }
.action-dialog__intro { display: flex; margin-bottom: 18px; align-items: center; gap: 12px; }
.action-dialog__intro > span { display: grid; width: 42px; height: 42px; flex: 0 0 auto; place-items: center; border-radius: 11px; font-size: 18px; }
.action-dialog__intro > span.blue { color: #1f6bff; background: #eaf1ff; }
.action-dialog__intro > span.green { color: #0e9f5c; background: #e8f6ee; }
.action-dialog__intro > span.orange { color: #d97706; background: #fff3df; }
.action-dialog__intro > span.purple { color: #805ad5; background: #f1ebfc; }
.action-dialog__intro strong, .action-dialog__intro small { display: block; }
.action-dialog__intro strong { font-size: 13px; }
.action-dialog__intro small { margin-top: 3px; color: #8a90a0; font-size: 11px; }
.up-action-dialog label { display: block; margin: 14px 0 7px; color: #4a5060; font-size: 11.5px; font-weight: 700; }
.up-point-card-dialog .el-dialog__header {
  padding: 22px 28px 14px;
}
.up-point-card-dialog .el-dialog__title {
  color: #171c28;
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
}
.up-point-card-dialog .el-dialog__headerbtn {
  top: 25px;
  right: 22px;
  font-size: 16px;
}
.up-point-card-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #9299a8;
}
.up-point-card-dialog .el-dialog__body { padding: 20px 28px 4px; }
.up-point-card-dialog .el-dialog__footer { padding: 14px 28px 24px; }
.point-card-account {
  display: flex;
  height: 46px;
  padding: 0 14px;
  align-items: center;
  justify-content: space-between;
  color: #5f6675;
  border: 1px solid #e7eaf0;
  border-radius: 9px;
  background: #f7f8fb;
  font-size: 13px;
  font-weight: 600;
}
.point-card-account strong { color: #252b36; font-size: 13px; font-weight: 700; }
.up-point-card-dialog label {
  margin-top: 16px;
  color: #4d5565;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
}
.up-point-card-dialog .point-card-select .el-input__inner {
  height: 43px;
  color: #343b49;
  border-color: #dce1e9;
  line-height: 43px;
  font-size: 14px;
}
.up-point-card-dialog .point-card-select .el-input__inner::placeholder {
  color: #a8afbc;
}
.point-card-state { margin-top: 8px; color: #98a0af; font-size: 11px; }
.point-card-estimate {
  margin: 14px 0 16px;
  color: #79808f;
  font-size: 13px;
  line-height: 20px;
}
.point-card-estimate strong { color: #1f6bff; font-size: 16px; font-weight: 800; }
.point-card-estimate span { margin-left: 3px; }
.point-card-notice {
  padding: 13px 14px;
  color: #1f6bff;
  border-radius: 8px;
  background: #edf4ff;
  font-size: 13px;
  line-height: 20px;
}
.transfer-mobile-flow { display: none; }
.verification-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px; }
.verification-target { display: block; margin-top: 7px; color: #98a0af; font-size: 10.5px; }
.transfer-available { display: flex; margin-top: 7px; justify-content: flex-end; gap: 3px; color: #79808f; font-size: 11.5px; }
.transfer-available strong { color: #1f6bff; font-weight: 700; }
.withdraw-available { display: flex; margin-top: 7px; justify-content: flex-end; gap: 3px; color: #79808f; font-size: 11.5px; }
.withdraw-available strong { color: #1f6bff; font-weight: 700; }
.internal-transfer-fee { display: flex; margin-top: 10px; align-items: center; gap: 8px; color: #79808f; font-size: 11.5px; }
.internal-transfer-fee del { color: #98a0af; }
.internal-transfer-fee strong { color: #0e9f5c; font-weight: 700; }
.up-action-dialog .el-input__inner { border-radius: 8px; }
.up-action-dialog .transfer-fixed-select .el-input.is-disabled .el-input__inner {
  color: #343b49;
  border-color: #dce1e9;
  background: #fff;
  -webkit-text-fill-color: #343b49;
  opacity: 1;
  cursor: default;
}
.up-action-dialog .transfer-fixed-select .el-input.is-disabled .el-input__icon {
  color: #8a90a0;
  cursor: default;
}
.up-action-dialog .has-all-button .el-input__inner { padding-right: 104px; }
.action-dialog__amount-suffix { display: inline-flex; height: 40px; align-items: center; }
.action-dialog__unit { padding-right: 7px; color: #8a90a0; line-height: 40px; font-size: 11px; }
.action-dialog__all { height: 24px; padding: 0 8px; color: #1f6bff; border: 0; border-left: 1px solid #dce3ef; outline: none; background: transparent; cursor: pointer; font-size: 12px; font-weight: 700; }
.action-dialog__all:disabled { color: #aeb5c2; cursor: not-allowed; }
.action-dialog__hint { margin: 10px 0 0; color: #98a0af; font-size: 10.5px; }
.dialog-footer { display: flex; justify-content: flex-end; gap: 9px; }
.dialog-footer button { min-width: 90px; }
.dialog-footer.point-card-footer button {
  height: 44px;
  padding: 0 20px;
  flex: 1;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 700;
}
.dialog-footer button:disabled { cursor: wait; opacity: .6; }
</style>
